export type ProjectImage = {
  src: string
  alt: string
}

export type Initiative = {
  slug: string
  name: string
  category: string
  status: string
  description: string
  /** Secondary framing shown on the Projects page. */
  summary: string
  /** Brief development-stage line, shown quietly within the introduction. */
  stage: string
  cta: string
  href: string
  image?: ProjectImage
}

export const cognocracyEmblem: ProjectImage = {
  src: 'https://cdn.magicpatterns.com/uploads/iXpXHQbF6kpmfU4rAP7pkL/Monochrome_emblem_with_ascending_steps.png',
  alt: 'Cognocracy emblem representing connected judgment, civic participation and human development.',
}

export const initiatives: Initiative[] = [
  {
    slug: 'cognocracy',
    name: 'Cognocracy',
    category: 'Civic Governance',
    status: 'Concept Proposal',
    description:
      'A civic framework connecting human development, demonstrated judgment and readiness for public responsibility.',
    summary:
      'Cognocracy connects SyNeural’s technology with a broader civic framework. Its associated government solution is NCDS.',
    stage: 'A SyNeural project under development.',
    cta: 'Explore Cognocracy',
    href: '/projects/cognocracy',
    image: cognocracyEmblem,
  },
]

export type ProjectMaterial = {
  title: string
  summary: string
  status: string
  href: string
}

/** Placeholder papers: planned documents, not published work. */
export const cognocracyPapers: ProjectMaterial[] = [
  {
    title: 'Cognocracy: a concept note',
    summary: 'The purpose, scope and open questions of capability-informed civic governance.',
    status: 'In preparation',
    href: '/roadmap#publications',
  },
  {
    title: 'Cognitive Social Capital',
    summary: 'How demonstrated reasoning and responsible action could become recognizable signals of readiness.',
    status: 'In preparation',
    href: '/roadmap#publications',
  },
  {
    title: 'Designing a portable capability credential',
    summary: 'What a credential would record, how it could be shared, and the limits on its use.',
    status: 'Planned',
    href: '/roadmap#publications',
  },
  {
    title: 'The Cognarchic State',
    summary: 'The broader institutional vision behind Cognocracy and its relationship to NCDS.',
    status: 'Planned',
    href: '/research',
  },
]

/** Assessment methods the project draws on, with their current status. */
export const cognocracyMethods: ProjectMaterial[] = [
  {
    title: 'NCJI five-pillar model',
    summary: 'The working architecture for civic judgment assessment and development.',
    status: 'Working model',
    href: '/ncji',
  },
  {
    title: 'CJI assessment methods',
    summary: 'Structured reasoning and judgment tasks, criteria and interpretation guidance.',
    status: 'Under development',
    href: '/cji',
  },
  {
    title: 'HCF development cycle',
    summary: 'Assess → Review → Develop → Reassess, with human review at each step.',
    status: 'Working model',
    href: '/human-capability',
  },
  {
    title: 'Pilot assessment protocol',
    summary: 'How reliability, validity and real-world usefulness would be tested.',
    status: 'Planned',
    href: '/roadmap#publications',
  },
]

export const cognocracyComponents = [
  {
    name: 'SKP',
    classification: 'Knowledge infrastructure',
    body: 'Connects assessment methods, supporting evidence, terminology, and provenance.',
    href: '/semantic-knowledge',
    linkLabel: 'Explore SKP',
  },
  {
    name: 'Human Capability Framework',
    classification: 'Core assessment and development framework',
    body: 'Defines the assessment and development methods used by CJI and NCJI.',
    href: '/human-capability',
    linkLabel: 'Explore the framework',
  },
  {
    name: 'Noetivra',
    classification: 'Visual, interactive workspace',
    body: 'Provides the interactive workspace for reviewing capability profiles, following development pathways, and managing credentials.',
    href: '/noetivra',
    linkLabel: 'Explore Noetivra',
  },
]

export const credentialFields = [
  'Assessed capabilities',
  'Standards met',
  'Assessment date and validity period',
  'Relevant application context',
  'Development priorities',
  'Limitations and uncertainty',
]

export const cognocracyApplications = [
  {
    name: 'Professional Development',
    body: 'Identify strengths and support targeted development.',
  },
  {
    name: 'Leadership Readiness',
    body: 'Inform preparation and evaluation for designated positions of responsibility.',
  },
  {
    name: 'Digital Communities',
    body: 'Enable optional sharing of verified capability credentials.',
  },
  {
    name: 'Civic Governance Research',
    body: 'Examine how demonstrated capability could inform institutional roles and decision-making.',
  },
]

export const cognocracyOpenQuestions = [
  'Assessment validity and real-world usefulness.',
  'Fairness across languages, cultures, disabilities, and socioeconomic circumstances.',
  'Transparent criteria and independent oversight.',
  'Privacy and participant control over disclosure.',
  'Human review and meaningful appeals.',
  'Protection against ideological conformity influencing scores.',
  'Limits on using credentials to determine civic participation or essential access.',
]
